import React from "react";
import { Column } from "../components/Layout";
import { Signature } from "../components/Signature";
import { FadeIn } from "../components/FadeIn";
import { Seo } from "../components/Seo";
import { OutlineLink } from "../components/OutlineLink";

const MOVEMENTS = [
  {
    numeral: "One",
    title: "The Conversations",
    body: [
      "Before the anthropologist ever arrives, the work begins with listening. Once a week, for an hour or two, at whatever time suits the subject, a long recorded conversation takes place. The subject tells. The anthropologist listens. Nothing is rigid: the pace is the subject's, rest is built in, and any week may be skipped, any subject left for another day. There is nothing to prepare and nothing to get right.",
      "To be listened to at length, every week, by someone who has come for nothing but the story, is rare at any age and rarest in later life. The commission begins by restoring it. Every conversation is kept to archival standard, and the voice itself, preserved across these months, may become part of what the family receives.",
    ],
  },
  {
    numeral: "Two",
    title: "Archival Systematisation",
    body: "The archive is gathered while the conversations run, so that the papers and the voice inform each other from the first month. We gather, digitise, and read the entire written record of a life: decades of private papers, diaries, correspondence, and digital traces, treated as a single historical text. Before a word is written, the life is assembled into something that can be studied whole.",
  },
  {
    numeral: "Three",
    title: "The Participant-Observed Residency",
    body: "We come, and we stay. The anthropologist enters the daily environment of the subject and attends to how a life is actually lived, the tacit and unspoken realities that no interview surfaces on its own, from boardroom judgment to the quiet of a morning garden. Presence is the method. It is also what no one else offers.",
  },
  {
    numeral: "Four",
    title: "Social Network Triangulation",
    body: "We conduct deep, recorded encounters with the circle around the subject: peers, colleagues, rivals, and family. A life is defined by the shape it leaves in the people standing around it, and that shape can only be mapped from the outside in.",
  },
  {
    numeral: "Five",
    title: "Dual-Response Visual Ethnography",
    body: "Personal photographs are treated as analytical evidence and read twice: once for what they hold, and once for what our looking brings to them. The seam between subject and observer is never hidden. It is the honesty of the work made visible.",
  },
];

const Methodology = () => {
  return (
    <div data-testid="methodology-page">
      <Seo
        title="The Life Study Method · The Raven's Quill"
        description="Five instruments of ethnographic fieldwork, the conversations, the archive, the residency, the circle, and the image, applied to a single extraordinary life."
        path="/the-method"
      />
      <section className="pt-20 pb-16 md:pt-28 md:pb-20">
        <Column>
          <Signature label="The Method" testid="methodology-signature" />
          <FadeIn>
            <h1 className="text-[32px] md:text-[38px] italic leading-[1.25] text-[#1C1A17]" data-testid="methodology-headline">
              The Five Movements of Looking
            </h1>
          </FadeIn>
          <FadeIn as="p" className="mt-10 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            An anthropologist does not simply transcribe an interview. The
            Raven&rsquo;s Quill reads a single life through five instruments. Each
            deepens the others. None could carry the life alone. This is the method
            that separates a study from a memoir. No telling is put on trial here, least
            of all the subject&rsquo;s own. A life is simply larger than any one account
            of it, and the work exists to hold its truths together on the same page,
            with the subject&rsquo;s voice given first. It is the method of The Life
            Study.
          </FadeIn>
        </Column>
      </section>

      <section className="pb-16">
        <Column>
          <div className="space-y-16 md:space-y-20">
            {MOVEMENTS.map((m) => (
              <FadeIn key={m.numeral} data-testid={`movement-${m.numeral.toLowerCase()}`}>
                <div className="flex flex-col sm:flex-row sm:gap-10">
                  <div className="mb-4 sm:mb-0 sm:w-[120px] shrink-0">
                    <span className="text-[11px] uppercase tracking-[0.3em] text-[#7A2E2E]">
                      {m.numeral}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-[22px] md:text-[24px] italic text-[#1C1A17]">
                      {m.title}
                    </h2>
                    {(Array.isArray(m.body) ? m.body : [m.body]).map((para, i) => (
                      <p key={i} className="mt-5 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                        {para}
                      </p>
                    ))}
                    {m.image && (
                      <figure className="mt-8 max-w-[380px]" data-testid={`movement-${m.numeral.toLowerCase()}-image`}>
                        <img
                          src={m.image}
                          alt={m.imageAlt}
                          className="w-full h-auto border border-[#7A2E2E] p-2 sm:p-3"
                          loading="lazy"
                        />
                        {m.caption && (
                          <figcaption className="mt-4 text-[13px] italic leading-[1.6] text-[#6E6A5E]">
                            {m.caption}
                          </figcaption>
                        )}
                      </figure>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </Column>
      </section>

      <section className="py-16 md:py-24">
        <Column>
          <FadeIn as="p" className="text-[18px] md:text-[20px] italic leading-[1.6] text-[#1C1A17]">
            The five instruments do not run in sequence and stop. They hold a life in
            tension until it can be written truthfully, and that is what the months of
            the engagement are for.
          </FadeIn>
          <FadeIn className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5">
            <OutlineLink to="/commission" testid="methodology-cta">
              Request a Private Consultation
            </OutlineLink>
            <OutlineLink to="/from-the-page" variant="oxblood" testid="methodology-from-the-page-cta">
              Read a Page of the Writing
            </OutlineLink>
          </FadeIn>
        </Column>
      </section>
    </div>
  );
};

export default Methodology;
